/**
 * Property staff transfer — Angular StaffTransferComponent parity for property edit contacts.
 * Selected contacts transfer to a chosen company + property via POST /api/transfer_roles.
 */
import { useState, useEffect, useCallback } from "react"
import apiClient from "@/lib/api/apiClient"
import { OnScreenModal } from "@/components/ui/OnScreenModal"
import {
  primaryEntityActionButtonClass,
  secondaryEntityActionButtonClass,
} from "@/components/ui/entityActionButtonClasses"
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/SearchableSelect"
import { showSuccess, showApiError } from "@/lib/utils/notifications"
import { formatPhoneDisplay, maskPhoneOnChange, PROPERTY_PHONE_INPUT_MAX_LENGTH } from "../utils/formatPhone"
import { getRoleDisplayName } from "@/constants/roles"
import { cn } from "@/lib/utils/helpers"
import { MOBILE_PATTERN, EMAIL_PATTERN } from "../constants"
import type { CompanyStaffMember } from "@/features/company/types"
import type { PropertyStaffTransferModalProps } from "../types"

function staffName(staff: CompanyStaffMember): string {
    return staff.name || `${staff.first_name || ""} ${staff.last_name || ""}`.trim() || "—"
}

export function PropertyStaffTransferModal({
    isOpen,
    onClose,
    propertyId,
    property,
    onSuccess,
}: PropertyStaffTransferModalProps) {
    const [transferCompanyList, setTransferCompanyList] = useState<SearchableSelectOption[]>([])
    const [transferPropertyList, setTransferPropertyList] = useState<SearchableSelectOption[]>([])
    const [transferCompanyId, setTransferCompanyId] = useState("")
    const [transferPropertyId, setTransferPropertyId] = useState("")
    const [transferSelectedRoleIds, setTransferSelectedRoleIds] = useState<number[]>([])
    const [transferStaff, setTransferStaff] = useState<CompanyStaffMember[]>([])
    const [transferStaffEdits, setTransferStaffEdits] = useState<Record<number, { mobile_no: string; email: string }>>({})
    const [transferErrors, setTransferErrors] = useState<Record<number, { mobile_no?: string; email?: string }>>({})
    const [transferring, setTransferring] = useState(false)
    const [transferLoading, setTransferLoading] = useState(false)

    const resetForm = useCallback(() => {
        setTransferCompanyId("")
        setTransferPropertyId("")
        setTransferSelectedRoleIds([])
        setTransferStaffEdits({})
        setTransferErrors({})
        setTransferPropertyList([])
    }, [])

    const loadTransferCompanies = useCallback(async (search?: string) => {
        try {
            const params: Record<string, string> = {}
            if (search) params.q = search
            const res = await apiClient.get("/api/all_company", { params })
            const list = (Array.isArray(res.data) ? res.data : []) as Record<string, unknown>[]
            setTransferCompanyList(
                list.map((c) => ({
                    value: String(c.company_id),
                    label: String(c.company_name ?? ""),
                })),
            )
            // Angular `getCompanies`: auto-select only when the API returns a single company.
            if (list.length === 1) {
                setTransferCompanyId(String(list[0].company_id))
            }
        } catch {
            /* ignore */
        }
    }, [])

    const loadTransferProperties = useCallback(async (companyId: string, search?: string) => {
        try {
            const params: Record<string, string> = {}
            if (search) params.q = search
            if (companyId) params.company_id = companyId
            const res = await apiClient.get("/api/all_property", { params })
            const list = (Array.isArray(res.data) ? res.data : []) as Record<string, unknown>[]
            setTransferPropertyList(
                list.map((p) => ({
                    value: String(p.property_id),
                    label: String(p.property_name ?? ""),
                })),
            )
            if (list.length === 1) setTransferPropertyId(String(list[0].property_id))
        } catch {
            /* ignore */
        }
    }, [])

    const loadTransferStaff = useCallback(async () => {
        setTransferLoading(true)
        try {
            const res = await apiClient.get("/api/property_staff", { params: { property_id: propertyId, sortBy: "desc" } })
            setTransferStaff(Array.isArray(res.data) ? res.data : [])
        } catch (err) {
            showApiError(err)
            setTransferStaff([])
        } finally {
            setTransferLoading(false)
        }
    }, [propertyId])

    useEffect(() => {
        if (!isOpen) return
        resetForm()
        void loadTransferCompanies()
        void loadTransferStaff()
    }, [isOpen, resetForm, loadTransferCompanies, loadTransferStaff])

    useEffect(() => {
        if (!isOpen || !transferCompanyId) return
        void loadTransferProperties(transferCompanyId)
    }, [isOpen, transferCompanyId, loadTransferProperties])

    const handleTransferCompanyChange = (companyId: string) => {
        setTransferCompanyId(companyId)
        setTransferPropertyId("")
        setTransferPropertyList([])
        if (companyId) void loadTransferProperties(companyId)
    }

    const toggleTransferRoleId = (roleId: number) => {
        setTransferSelectedRoleIds((prev) =>
            prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId],
        )
    }

    const selectAllTransfer = (checked: boolean) => {
        if (checked) {
            setTransferSelectedRoleIds(
                transferStaff
                    .map((s) => s.role_id)
                    .filter((id): id is number => id != null && !Number.isNaN(Number(id)))
                    .map(Number),
            )
        } else {
            setTransferSelectedRoleIds([])
        }
    }

    const handleTransferSubmit = async () => {
        if (!transferSelectedRoleIds.length) return
        const errs: Record<number, { mobile_no?: string; email?: string }> = {}
        let hasErrors = false
        for (const roleId of transferSelectedRoleIds) {
            const staff = transferStaff.find((s) => Number(s.role_id) === roleId)
            const edits = transferStaffEdits[roleId] || {
                mobile_no: (staff?.mobile_no as string) || "",
                email: (staff?.email as string) || "",
            }
            const rowErr: { mobile_no?: string; email?: string } = {}
            const digits = edits.mobile_no.replace(/\D/g, "")
            if (!digits) {
                rowErr.mobile_no = "Phone is required"
                hasErrors = true
            } else if (!MOBILE_PATTERN.test(digits)) {
                rowErr.mobile_no = "Invalid phone number"
                hasErrors = true
            }
            if (!edits.email.trim()) {
                rowErr.email = "Email is required"
                hasErrors = true
            } else if (!EMAIL_PATTERN.test(edits.email.trim())) {
                rowErr.email = "Invalid email address"
                hasErrors = true
            }
            if (rowErr.mobile_no || rowErr.email) errs[roleId] = rowErr
        }
        setTransferErrors(errs)
        if (hasErrors) return

        setTransferring(true)
        try {
            const updateUserinfo: Record<string, unknown>[] = []
            for (const roleId of transferSelectedRoleIds) {
                const edits = transferStaffEdits[roleId]
                if (edits) {
                    const staff = transferStaff.find((s) => Number(s.role_id) === roleId)
                    if (staff) {
                        updateUserinfo.push({
                            role_id: roleId,
                            mobile_no: edits.mobile_no.replace(/\D/g, ""),
                            email: edits.email,
                        })
                    }
                }
            }
            await apiClient.post("/api/transfer_roles", {
                type: "PROPERTY",
                role_ids: transferSelectedRoleIds,
                company_id: transferCompanyId ? Number(transferCompanyId) : undefined,
                property_id: transferPropertyId ? Number(transferPropertyId) : undefined,
                update_userinfo: updateUserinfo,
            })
            showSuccess("Role Transfer Successfully!")
            onClose()
            onSuccess?.()
        } catch (err) {
            showApiError(err)
        } finally {
            setTransferring(false)
        }
    }

    return (
        <OnScreenModal isOpen={isOpen} title="Staff Transfer" onClose={onClose} maxWidth="max-w-3xl">
            <div className="space-y-4">
                <p className="text-center text-sm font-medium text-slate-600">
                    Selected contact will be transferred to selected property
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Current Company</label>
                        <p className="font-semibold text-sm">{property?.company_name as string || "—"}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Transfer to Company</label>
                        <SearchableSelect
                            placeholder="Select Company"
                            options={transferCompanyList}
                            value={transferCompanyId}
                            onChange={(v) => handleTransferCompanyChange(Array.isArray(v) ? v[0] : v)}
                            onSearch={(s) => void loadTransferCompanies(s)}
                            triggerDataTestId="property-contacts-transfer-company-select"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Current Property</label>
                        <p className="font-semibold text-sm">{property?.property_name as string || "—"}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Transfer to Property</label>
                        <SearchableSelect
                            placeholder="Select Property"
                            options={transferPropertyList}
                            value={transferPropertyId}
                            onChange={(v) => setTransferPropertyId(Array.isArray(v) ? v[0] : v)}
                            onSearch={(s) => transferCompanyId && void loadTransferProperties(transferCompanyId, s)}
                            triggerDataTestId="property-contacts-transfer-property-select"
                        />
                    </div>
                </div>

                {transferStaff.length > 0 && (
                    <div className="overflow-x-auto border border-slate-200 rounded-md">
                        <table className="w-full text-sm" data-testid="property-contacts-transfer-table">
                            <thead className="bg-slate-100 text-slate-600">
                                <tr>
                                    <th className="px-4 py-2 w-10">
                                        <input
                                            type="checkbox"
                                            onChange={(e) => selectAllTransfer(e.target.checked)}
                                            data-testid="property-contacts-transfer-select-all-checkbox"
                                            checked={
                                                transferSelectedRoleIds.length === transferStaff.length &&
                                                transferStaff.length > 0
                                            }
                                        />
                                        <span className="ml-2 text-xs">Select All</span>
                                    </th>
                                    <th className="px-4 py-2 text-left font-medium">Name</th>
                                    <th className="px-4 py-2 text-left font-medium">Role</th>
                                    <th className="px-4 py-2 text-left font-medium">Phone Number</th>
                                    <th className="px-4 py-2 text-left font-medium">Email</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                                {transferStaff.map((s) => {
                                    const rid = Number(s.role_id)
                                    const isSelected = transferSelectedRoleIds.includes(rid)
                                    const edits = transferStaffEdits[rid] || {
                                        mobile_no: (s.mobile_no as string) || "",
                                        email: (s.email as string) || "",
                                    }
                                    return (
                                        <tr key={rid} className="hover:bg-slate-50">
                                            <td className="px-4 py-2">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleTransferRoleId(rid)}
                                                    data-testid={`property-contacts-transfer-row-${rid}-checkbox`}
                                                />
                                            </td>
                                            <td className="px-4 py-2 text-slate-800">{staffName(s)}</td>
                                            <td className="px-4 py-2 text-slate-600">
                                                {s.role_name ? getRoleDisplayName(s.role_name) : "—"}
                                            </td>
                                            <td className="px-4 py-2">
                                                {isSelected ? (
                                                    <div>
                                                        <input
                                                            type="text"
                                                            value={edits.mobile_no}
                                                            maxLength={PROPERTY_PHONE_INPUT_MAX_LENGTH}
                                                            data-testid={`property-contacts-transfer-row-${rid}-phone-input`}
                                                            onChange={(e) => {
                                                                const v = maskPhoneOnChange(e.target.value)
                                                                setTransferStaffEdits((prev) => ({
                                                                    ...prev,
                                                                    [rid]: { ...edits, mobile_no: v },
                                                                }))
                                                                setTransferErrors((prev) => {
                                                                    const next = { ...prev }
                                                                    if (next[rid]) {
                                                                        delete next[rid].mobile_no
                                                                        if (!next[rid].email) delete next[rid]
                                                                    }
                                                                    return next
                                                                })
                                                            }}
                                                            className={cn(
                                                                "w-full px-2 py-1 border rounded text-sm",
                                                                transferErrors[rid]?.mobile_no
                                                                    ? "border-rose-400"
                                                                    : "border-slate-200",
                                                            )}
                                                            placeholder="Phone"
                                                        />
                                                        {transferErrors[rid]?.mobile_no && (
                                                            <p className="text-xs text-rose-600 mt-0.5">
                                                                {transferErrors[rid].mobile_no}
                                                            </p>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-slate-600">
                                                        {formatPhoneDisplay(s.mobile_no as string)}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2">
                                                {isSelected ? (
                                                    <div>
                                                        <input
                                                            type="text"
                                                            value={edits.email}
                                                            data-testid={`property-contacts-transfer-row-${rid}-email-input`}
                                                            onChange={(e) => {
                                                                setTransferStaffEdits((prev) => ({
                                                                    ...prev,
                                                                    [rid]: { ...edits, email: e.target.value },
                                                                }))
                                                                setTransferErrors((prev) => {
                                                                    const next = { ...prev }
                                                                    if (next[rid]) {
                                                                        delete next[rid].email
                                                                        if (!next[rid].mobile_no) delete next[rid]
                                                                    }
                                                                    return next
                                                                })
                                                            }}
                                                            className={cn(
                                                                "w-full px-2 py-1 border rounded text-sm",
                                                                transferErrors[rid]?.email
                                                                    ? "border-rose-400"
                                                                    : "border-slate-200",
                                                            )}
                                                            placeholder="Email"
                                                        />
                                                        {transferErrors[rid]?.email && (
                                                            <p className="text-xs text-rose-600 mt-0.5">
                                                                {transferErrors[rid].email}
                                                            </p>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-slate-600">{String(s.email ?? "—")}</span>
                                                )}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="flex w-full flex-row flex-wrap items-center justify-center gap-2 pt-2">
                    <button
                        type="button"
                        onClick={() => void handleTransferSubmit()}
                        disabled={
                            transferring ||
                            transferLoading ||
                            !transferSelectedRoleIds.length ||
                            !transferPropertyId
                        }
                        data-testid="property-contacts-transfer-submit-button"
                        className={primaryEntityActionButtonClass}
                    >
                        Transfer
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="property-contacts-transfer-cancel-button"
                        className={secondaryEntityActionButtonClass}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </OnScreenModal>
    )
}
